import { useEffect, useRef, useState } from "react";
import { Loader2, RotateCcw, Send, Sparkles, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "sk_ai_session";
const MESSAGES_KEY = "sk_ai_messages";

const WELCOME = {
  role: "assistant",
  content:
    "Nanga def ! Je suis Teranga, l'assistant Sama-Keur. Je vous aide à choisir un mitigeur, un meuble de salle de bain ou un lit coffre — dites-moi ce que vous cherchez. (Mén naa la wolof !)",
};

const SUGGESTIONS = [
  "Je cherche un lit coffre",
  "Mon budget : 100 000 FCFA",
  "Wax ma ngeen wolof ?",
];

const loadMessages = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(MESSAGES_KEY));
    return Array.isArray(saved) && saved.length ? saved : [WELCOME];
  } catch {
    return [WELCOME];
  }
};

const AiChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadMessages);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(localStorage.getItem(SESSION_KEY) || null);
  const scrollRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages.slice(-40)));
    } catch {
      /* stockage local indisponible */
    }
  }, [messages]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages, open]);

  const patchLastAssistant = (fn) =>
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      next[next.length - 1] = { role: "assistant", content: fn(last?.content || "") };
      return next;
    });

  const send = async (e, preset) => {
    e?.preventDefault();
    const text = (preset || input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionRef.current }),
      });
      const sid = res.headers.get("X-Session-Id");
      if (sid) {
        sessionRef.current = sid;
        localStorage.setItem(SESSION_KEY, sid);
      }
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let payload;
          try {
            payload = JSON.parse(line.slice(5));
          } catch {
            continue;
          }
          if (payload.t) patchLastAssistant((c) => c + payload.t);
          else if (payload.error) patchLastAssistant(() => payload.error);
        }
      }
    } catch {
      patchLastAssistant((c) => c || "L'assistant est momentanément indisponible. Réessayez dans un instant.");
    } finally {
      setStreaming(false);
    }
  };

  const reset = () => {
    localStorage.removeItem(SESSION_KEY);
    sessionRef.current = null;
    setMessages([WELCOME]);
  };

  return (
    <>
      <motion.button
        data-testid="ai-chat-toggle-button"
        onClick={() => setOpen((o) => !o)}
        initial={{ opacity: 0, scale: 0.6, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 1.3, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.08 }}
        className="fixed bottom-6 left-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-terracotta text-sand shadow-lg shadow-bark/25 transition-colors hover:bg-terracotta-dark"
        aria-label="Discuter avec l'assistant Teranga"
      >
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 left-6 z-40 flex h-[30rem] w-[calc(100vw-3rem)] max-w-sm flex-col overflow-hidden border border-clay bg-cream shadow-2xl shadow-bark/30"
          >
            <div className="flex items-center justify-between bg-terracotta px-5 py-4">
              <div>
                <p className="font-display text-lg font-semibold text-sand">Teranga</p>
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-sand/70">Assistant Sama-Keur</p>
              </div>
              <button
                data-testid="ai-chat-reset-button"
                onClick={reset}
                className="flex h-8 w-8 items-center justify-center rounded-full text-sand/70 transition-colors hover:bg-sand/10 hover:text-sand"
                aria-label="Nouvelle conversation"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            </div>

            <div ref={scrollRef} data-testid="ai-chat-messages" className="flex-1 space-y-3 overflow-y-auto p-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-line ${
                      m.role === "user"
                        ? "bg-bark text-sand"
                        : "border border-clay bg-white text-bark"
                    }`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 border-t border-clay bg-white px-3 pt-3" data-testid="ai-chat-suggestions">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`ai-chat-suggestion-${i}`}
                    onClick={() => send(null, s)}
                    disabled={streaming}
                    className="rounded-full border border-clay px-3 py-1.5 text-xs text-bark/70 transition-colors hover:border-terracotta hover:text-terracotta disabled:opacity-40"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={send} className={`flex items-center gap-2 bg-white p-3 ${messages.length > 1 ? "border-t border-clay" : ""}`}>
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Votre question…"
                maxLength={2000}
                className="h-11 flex-1 rounded-md border border-clay bg-cream px-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none"
              />
              <button
                data-testid="ai-chat-send-button"
                type="submit"
                disabled={streaming || !input.trim()}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-terracotta text-sand transition-colors hover:bg-terracotta-dark disabled:opacity-40"
                aria-label="Envoyer"
              >
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AiChatWidget;
